import { Modal, Text } from '@kula-ai/components';
import { useShowToast } from '@/hooks/useShowToast';
import { useConsentLifecycleAction } from './api';
import { CONSENT_ACTION_SUCCESS, consentErrorMessage } from './consentStatus';
import type { ConsentTarget } from './types';

interface RequestConsentModalProps {
  target: ConsentTarget;
  /** Menu label — "Request consent" or "Request consent extension". */
  title: string;
  /** Body copy, chosen by first-request vs extension. */
  description: string;
  isOpen: boolean;
  onClose: () => void;
  invalidateKeys?: unknown[][];
  onSuccess?: () => void;
}

// Confirm before emailing a candidate a consent (or extension) request, so the
// send isn't a one-click accident — mirrors the other consent-action modals.
export const RequestConsentModal = ({
  target,
  title,
  description,
  isOpen,
  onClose,
  invalidateKeys,
  onSuccess,
}: RequestConsentModalProps) => {
  const showToast = useShowToast();
  const lifecycle = useConsentLifecycleAction({ invalidateKeys });

  const handleConfirm = () =>
    lifecycle.mutate(
      { action: 'request', target },
      {
        onSuccess: () => {
          showToast({
            title: CONSENT_ACTION_SUCCESS.request,
            status: 'success',
          });
          onSuccess?.();
          onClose();
        },
        onError: (err) =>
          showToast({ title: consentErrorMessage(err), status: 'error' }),
      }
    );

  return (
    <Modal
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
      size="md"
      title={title}
      primaryButton={{
        label: 'Send request',
        variant: 'primary',
        onClick: handleConfirm,
        isLoading: lifecycle.isPending,
        // kula Button doesn't derive disabled from isLoading — set it so a
        // second click can't fire another request mid-send.
        disabled: lifecycle.isPending,
        loadingText: 'Sending',
      }}
      secondaryButton={{
        label: 'Cancel',
        variant: 'secondary',
        onClick: onClose,
      }}
    >
      <Text variant="p" color="body">
        {description}
      </Text>
    </Modal>
  );
};
